import { useAuthContext, usePermissions } from '/src/hooks';
import { UpsellWall } from '/src/components/UpsellWall';
import { WebP } from '/src/components/WebP';
import { FEATURES } from '/src/features/_global/constants';

import FeaturedImagePNG from '@crazyegginc/hatch/dist/images/illustration-upsell-errors.png';
import FeaturedImageWebP from '@crazyegginc/hatch/dist/images/illustration-upsell-errors.webp';
import LeftLogoWebP from '@crazyegginc/hatch/dist/images/logo-g2crowd-high-performer.webp';
import LeftLogoPNG from '@crazyegginc/hatch/dist/images/logo-g2crowd-high-performer.png';

const TEXT_LIST = [
  {
    text: "Stop silent errors before they wreck your site's performance",
  },
  {
    text: "Find bugs instantly — don't wait for customer complaints",
  },
  {
    text: 'With clear, actionable reports, fix issues fast — no more guesswork',
  },
];

const TextContent = ({ canManageBilling, ownerEmail, isStripeUser }) => {
  return (
    <>
      <UpsellWall.Title
        titleText={
          canManageBilling
            ? 'Your site is at risk — fix JavaScript Errors now'
            : 'Request an upgrade to the Starter plan'
        }
      />
      {canManageBilling ? (
        <UpsellWall.List list={TEXT_LIST} noTextBelow={false} />
      ) : (
        <>
          <p className="mb-4 text-black-pearl-500 text-sm">Know if site errors are costing you conversions.</p>
          <p className="mb-6 text-black-pearl-500 text-sm">
            Contact your Account Admin at <strong>{ownerEmail}</strong> to upgrade and access Error Tracking.
          </p>
        </>
      )}

      {canManageBilling && (
        <p className="mb-6 text-md font-semibold text-black-pearl-500">
          Protect your site before visitors get frustrated and drop-off.
        </p>
      )}
      <UpsellWall.CTA
        metricType="errors_tracking_upsell_paywall"
        canManageBilling={canManageBilling}
        ownerEmail={ownerEmail}
        isStripeUser={isStripeUser}
      />
    </>
  );
};

const LeftContent = () => {
  return (
    <div className="flex">
      <WebP
        webp={LeftLogoWebP}
        fallback={LeftLogoPNG}
        width="80"
        height="84"
        alt="high performer winter 2024"
        className="mr-5"
      />
      <div className="flex-1">
        <UpsellWall.TestimonialText textClassNames="mb-6">
          Their service is 2nd to none, I use heaps of marketing tech platforms...[With Crazy Egg, I get] tangible,
          practical support, fix bugs like nothing else.
        </UpsellWall.TestimonialText>
        <UpsellWall.TestimonialPerson
          name="Simon S., Validated G2 Reviewer"
          title="COO, Mid-Market (51-1000 employees)"
        />
      </div>
    </div>
  );
};

const RightContent = () => {
  return (
    <>
      <UpsellWall.TestimonialText textClassNames="mb-6">
        Crazy Egg captures usage data that can&apos;t be captured by basic analytics. Within my first week of using
        software, I was able to identify and fix a major usability issue with my website.
      </UpsellWall.TestimonialText>
      <UpsellWall.TestimonialPerson name="Validated G2 Reviewer" title="Enterprise ( > 1,000 employees)" />
    </>
  );
};

export function ErrorsUpsellPaywall() {
  const { currentAccount } = useAuthContext();

  const permissions = usePermissions();
  const canManageBilling = permissions.can('edit', FEATURES.BILLING).allowed;
  const isStripeUser = Boolean(currentAccount?.useStripe);

  return (
    <UpsellWall>
      <UpsellWall.TopPanel
        textContent={
          <TextContent
            canManageBilling={canManageBilling}
            ownerEmail={currentAccount.ownerEmail}
            isStripeUser={isStripeUser}
          />
        }
        imagePNG={FeaturedImagePNG}
        imageWebP={FeaturedImageWebP}
        imageWidth="523"
        imageHeight="300"
        imageAlt="errors feature"
        imgWrapperClassName="mt-auto"
      />
      <UpsellWall.BottomPanel isSeparate={false} leftContent={<LeftContent />} rightContent={<RightContent />} />
    </UpsellWall>
  );
}
